// PowerPoint rendering, moved verbatim from express's
// exportPresentation.handler.ts.
//
// The endpoint it backed was a pure function — the browser POSTed
// {title, slides, theme}, all of which it already had, and the only await in
// the handler was pptx.write(). Nothing was read from a database or object
// store, so the round-trip bought nothing except a dependency on a service
// being up.
//
// pptxgenjs runs unchanged in the browser; only the output type differs
// (a blob rather than a nodebuffer). The layout code below is otherwise the
// same, so decks keep rendering identically.
import { getThemeColors } from './themes';
// ============================================================================
// Master Slide Definitions
// ============================================================================
function defineMasterSlides(pptx, theme) {
    // Title Slide Master - Dark background with gradient effect
    pptx.defineSlideMaster({
        title: 'TITLE_SLIDE',
        background: {
            color: theme.primaryDark
        },
        objects: [
            // Subtle gradient overlay (simulated with shape)
            {
                rect: {
                    x: 0,
                    y: 0,
                    w: '100%',
                    h: '100%',
                    fill: {
                        color: theme.primaryDark,
                        transparency: 0
                    }
                }
            },
            // Left accent bar
            {
                rect: {
                    x: 0,
                    y: 0,
                    w: 0.15,
                    h: '100%',
                    fill: {
                        color: theme.primary
                    }
                }
            },
            // Bottom accent line
            {
                rect: {
                    x: 0.5,
                    y: 5.1,
                    w: 12.33,
                    h: 0.03,
                    fill: {
                        color: theme.primary
                    }
                }
            }
        ]
    });
    // Content Slide Master - Clean white with accent bar
    pptx.defineSlideMaster({
        title: 'CONTENT_SLIDE',
        background: {
            color: theme.background
        },
        objects: [
            // Top accent bar
            {
                rect: {
                    x: 0,
                    y: 0,
                    w: '100%',
                    h: 0.08,
                    fill: {
                        color: theme.primary
                    }
                }
            },
            // Left accent bar (thin)
            {
                rect: {
                    x: 0,
                    y: 0.08,
                    w: 0.06,
                    h: '100%',
                    fill: {
                        color: theme.primaryDark
                    }
                }
            }
        ]
    });
    // Section Header Master - For divider slides
    pptx.defineSlideMaster({
        title: 'SECTION_HEADER',
        background: {
            color: theme.background
        },
        objects: [
            // Large left accent bar
            {
                rect: {
                    x: 0,
                    y: 0,
                    w: 0.4,
                    h: '100%',
                    fill: {
                        color: theme.primary
                    }
                }
            },
            // Horizontal line
            {
                rect: {
                    x: 0.6,
                    y: 3.2,
                    w: 12.23,
                    h: 0.02,
                    fill: {
                        color: theme.primaryDark
                    }
                }
            }
        ]
    });
    // Closing Slide Master - Dark like title
    pptx.defineSlideMaster({
        title: 'CLOSING_SLIDE',
        background: {
            color: theme.primaryDark
        },
        objects: [
            // Center accent line
            {
                rect: {
                    x: 5,
                    y: 3.6,
                    w: 3.33,
                    h: 0.04,
                    fill: {
                        color: theme.secondary
                    }
                }
            }
        ]
    });
}
// ============================================================================
// Slide Rendering Functions
// ============================================================================
function renderTitleSlide(pptx, slideData, theme) {
    const slide = pptx.addSlide({
        masterName: 'TITLE_SLIDE'
    });
    // Main title
    slide.addText(slideData.title, {
        x: 0.6,
        y: 1.8,
        w: 12,
        h: 1.8,
        fontSize: 40,
        bold: true,
        color: theme.textOnDark,
        fontFace: 'Segoe UI',
        align: 'left',
        valign: 'bottom',
        wrap: true
    });
    // Subtitle
    if (slideData.subtitle) {
        slide.addText(slideData.subtitle, {
            x: 0.6,
            y: 3.7,
            w: 12,
            h: 0.6,
            fontSize: 20,
            color: theme.secondary,
            fontFace: 'Segoe UI',
            align: 'left'
        });
    }
    // Meta info box (placeholder for contract info)
    slide.addShape('rect', {
        x: 0.6,
        y: 4.5,
        w: 5,
        h: 1.2,
        fill: {
            color: theme.primaryDark,
            transparency: 50
        },
        line: {
            color: theme.primary,
            width: 1,
            dashType: 'solid'
        }
    });
}
function renderContentSlide(pptx, slideData, theme) {
    const slide = pptx.addSlide({
        masterName: 'CONTENT_SLIDE'
    });
    // Slide title
    slide.addText(slideData.title, {
        x: 0.5,
        y: 0.3,
        w: 12.33,
        h: 0.8,
        fontSize: 28,
        bold: true,
        color: theme.primary,
        fontFace: 'Segoe UI',
        valign: 'middle'
    });
    let contentY = 1.2;
    // Paragraphs
    if (slideData.content?.paragraphs && slideData.content.paragraphs.length > 0) {
        const paragraphText = slideData.content.paragraphs.join('\n\n');
        slide.addText(paragraphText, {
            x: 0.5,
            y: contentY,
            w: 12.33,
            h: 4.2,
            fontSize: 16,
            color: theme.text,
            fontFace: 'Segoe UI',
            valign: 'top',
            wrap: true
        });
        contentY += 2;
    }
    // Bullets
    if (slideData.content?.bullets && slideData.content.bullets.length > 0) {
        const bulletItems = [];
        for (const bullet of slideData.content.bullets){
            bulletItems.push({
                text: bullet.point,
                options: {
                    bullet: {
                        type: 'bullet'
                    },
                    indentLevel: 0,
                    fontSize: 16,
                    color: theme.text,
                    fontFace: 'Segoe UI',
                    paraSpaceAfter: 8
                }
            });
            // Sub-points
            if (bullet.subPoints) {
                for (const subPoint of bullet.subPoints){
                    bulletItems.push({
                        text: subPoint,
                        options: {
                            bullet: {
                                type: 'bullet',
                                characterCode: '–'
                            },
                            indentLevel: 1,
                            fontSize: 14,
                            color: theme.textLight,
                            fontFace: 'Segoe UI',
                            paraSpaceAfter: 4
                        }
                    });
                }
            }
        }
        slide.addText(bulletItems, {
            x: 0.5,
            y: contentY,
            w: 12.33,
            h: 5.5 - contentY,
            valign: 'top'
        });
        // Update contentY for tables that might follow
        contentY += Math.min(slideData.content.bullets.length * 0.4, 3);
    }
    // Tables
    if (slideData.content?.tables && slideData.content.tables.length > 0) {
        for (const table of slideData.content.tables){
            renderTable(slide, table, contentY, theme);
            // Move down for next table (estimate ~0.35 inches per row)
            contentY += 0.5 + table.rows.length * 0.35;
        }
    }
}
/**
 * Render a table on a slide
 */ function renderTable(slide, table, startY, theme) {
    const { headers, rows } = table;
    // Skip empty tables
    if (headers.length === 0 && rows.length === 0) return;
    // Calculate column widths - distribute evenly across slide width
    const tableWidth = 12.33;
    const numCols = Math.max(headers.length, rows[0]?.length || 1);
    const colWidth = tableWidth / numCols;
    // Build table data array for pptxgenjs
    const tableData = [];
    // Header row
    if (headers.length > 0) {
        const headerRow = headers.map((header)=>({
                text: header,
                options: {
                    bold: true,
                    color: theme.textOnDark,
                    fill: {
                        color: theme.tableHeader
                    },
                    fontFace: 'Segoe UI',
                    fontSize: 11,
                    align: 'left',
                    valign: 'middle'
                }
            }));
        tableData.push(headerRow);
    }
    // Data rows with alternating colors
    rows.forEach((row, rowIndex)=>{
        const isEvenRow = rowIndex % 2 === 1;
        const dataRow = row.map((cell)=>({
                text: cell,
                options: {
                    color: theme.text,
                    fill: {
                        color: isEvenRow ? theme.tableRowAlt : theme.background
                    },
                    fontFace: 'Segoe UI',
                    fontSize: 10,
                    align: 'left',
                    valign: 'middle'
                }
            }));
        tableData.push(dataRow);
    });
    // Calculate available height and limit rows if needed
    const maxHeight = 5.5 - startY;
    const rowHeight = 0.35;
    const maxRows = Math.floor(maxHeight / rowHeight);
    // Truncate if too many rows
    const displayData = tableData.slice(0, maxRows);
    // Add the table
    slide.addTable(displayData, {
        x: 0.5,
        y: startY,
        w: tableWidth,
        colW: Array(numCols).fill(colWidth),
        rowH: rowHeight,
        border: {
            type: 'solid',
            pt: 0.5,
            color: 'E2E8F0'
        },
        fontFace: 'Segoe UI'
    });
}
function renderSectionHeader(pptx, slideData, theme) {
    const slide = pptx.addSlide({
        masterName: 'SECTION_HEADER'
    });
    // Section title
    slide.addText(slideData.title, {
        x: 0.8,
        y: 2.2,
        w: 12,
        h: 1,
        fontSize: 36,
        bold: true,
        color: theme.primaryDark,
        fontFace: 'Segoe UI',
        align: 'left',
        valign: 'bottom'
    });
    // Subtitle if present
    if (slideData.subtitle) {
        slide.addText(slideData.subtitle, {
            x: 0.8,
            y: 3.4,
            w: 12,
            h: 0.6,
            fontSize: 18,
            color: theme.textLight,
            fontFace: 'Segoe UI',
            align: 'left'
        });
    }
}
function renderClosingSlide(pptx, slideData, theme) {
    const slide = pptx.addSlide({
        masterName: 'CLOSING_SLIDE'
    });
    // "Thank You" or title
    slide.addText(slideData.title, {
        x: 0.5,
        y: 2,
        w: 12.33,
        h: 1,
        fontSize: 44,
        bold: true,
        color: theme.textOnDark,
        fontFace: 'Segoe UI',
        align: 'center',
        valign: 'middle'
    });
    // Subtitle (project name, etc.)
    if (slideData.subtitle) {
        slide.addText(slideData.subtitle, {
            x: 0.5,
            y: 3.8,
            w: 12.33,
            h: 0.8,
            fontSize: 18,
            color: theme.secondary,
            fontFace: 'Segoe UI',
            align: 'center'
        });
    }
}
// ============================================================================
// Main Handler
// ============================================================================
/**
 * Build the .pptx for a deck. Returns the bytes; callers decide what to do
 * with them.
 *
 * pptxgenjs is imported dynamically so its ~1MB only reaches viewers who
 * actually export — the same reason ArtifactPreviewInline lazy-loads
 * Handsontable rather than shipping it to every chat message.
 */ export async function renderPptx(title, slides, themeName = 'federal-navy') {
    if (!title || slides.length === 0) {
        throw new Error('Title and slides are required');
    }
    const { default: Pptxgen } = await import('pptxgenjs');
    const theme = getThemeColors(themeName);
    const pptx = new Pptxgen();
    pptx.author = 'Generated by Specra';
    pptx.title = title;
    pptx.subject = 'Presentation';
    pptx.layout = 'LAYOUT_WIDE'; // 16:9 aspect ratio
    defineMasterSlides(pptx, theme);
    for (const slideData of slides){
        switch(slideData.type){
            case 'title':
                renderTitleSlide(pptx, slideData, theme);
                break;
            case 'section-header':
                renderSectionHeader(pptx, slideData, theme);
                break;
            case 'closing':
                renderClosingSlide(pptx, slideData, theme);
                break;
            default:
                renderContentSlide(pptx, slideData, theme);
                break;
        }
    }
    return await pptx.write({
        outputType: 'blob'
    });
}
